import { useState, useEffect } from 'react'
import { sanitizeInput, sanitizePhoneNumber } from '../utils/sanitizeInput'
import { useAuthStore } from '../store/authStore'
import { Phone, Calendar, AlertCircle, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { functions } from '../services/firebase'
import { httpsCallable } from 'firebase/functions'

export default function CompleteProfileModal({ onComplete }) {
  const { updateUserData, userData } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [isMobile, setIsMobile] = useState(false)
  
  const [formData, setFormData] = useState({
    phone: '',
    dateOfBirth: '',
    day: '',
    month: '',
    year: '',
  })

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    let sanitizedValue = value
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneNumber(value)
    } else {
      sanitizedValue = sanitizeInput(value)
    }
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    })
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' })
    }
  }

  const handleDateChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    }
    
    if (field === 'day' || field === 'month' || field === 'year') {
      const day = field === 'day' ? value : formData.day
      const month = field === 'month' ? value : formData.month
      const year = field === 'year' ? value : formData.year
      
      if (day && month && year) {
        newFormData.dateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
    }
    
    setFormData(newFormData)
    
    if (errors.dateOfBirth) {
      setErrors({ ...errors, dateOfBirth: '' })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Phone validation (10 digits)
    const phoneRegex = /^\d{10}$/
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'El teléfono debe tener 10 dígitos'
    }

    // Age validation (must be 18+)
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'La fecha de nacimiento es requerida'
    } else {
      const birthDate = new Date(formData.dateOfBirth)
      const today = new Date()
      const age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      
      if (age < 18 || (age === 18 && monthDiff < 0)) {
        newErrors.dateOfBirth = 'Debes ser mayor de 18 años'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const checkPhoneExists = async (phone) => {
    try {
      const checkPhone = httpsCallable(functions, 'checkPhoneExists');
      const result = await checkPhone({ 
        phone, 
        currentUserId: userData?.id 
      });
      return result.data.exists;
    } catch (error) {
      console.error('Error checking phone:', error);
      // En caso de error, permitir continuar (fail-safe)
      return false;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    // Verificar si el teléfono ya existe
    const phoneExists = await checkPhoneExists(formData.phone)
    if (phoneExists) {
      setErrors({ ...errors, phone: 'Este número de teléfono ya está registrado' })
      setIsLoading(false)
      toast.error('El número de teléfono ya está en uso')
      return
    }

    const result = await updateUserData({
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth,
      profileComplete: true,
    })
    
    setIsLoading(false)

    if (result.success) {
      toast.success('¡Perfil completado!')
      onComplete()
    } else {
      toast.error('Error al actualizar perfil')
    }
  }

  // Generar opciones para días, meses y años
  const days = Array.from({ length: 31 }, (_, i) => i + 1)
  const months = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ]
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="max-w-md w-full bg-[#222222] p-8 rounded-2xl shadow-2xl border border-[#0F3D2E]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#D4AF37]">
            Completa tu Perfil
          </h2>
        </div>

        <p className="text-[#BDBDBD] text-sm mb-6">
          Para continuar, necesitamos algunos datos adicionales.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-[#F8F8F8] mb-1">
              Teléfono *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="5512345678"
                maxLength="10"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                <AlertCircle size={14} /> {errors.phone}
              </p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
              Fecha de Nacimiento *
            </label>
            {isMobile ? (
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={formData.day}
                  onChange={(e) => handleDateChange('day', e.target.value)}
                  className={`input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Día</option>
                  {days.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <select
                  value={formData.month}
                  onChange={(e) => handleDateChange('month', e.target.value)}
                  className={`input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Mes</option>
                  {months.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
                <select
                  value={formData.year}
                  onChange={(e) => handleDateChange('year', e.target.value)}
                  className={`input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Año</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                />
              </div>
            )}
            
            {errors.dateOfBirth && (
              <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                <AlertCircle size={14} /> {errors.dateOfBirth}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] py-3 text-lg mt-6"
          >
            {isLoading ? 'Guardando...' : 'Continuar'}
          </button>
        </form>
      </div>
    </div>
  )
}
